// Dependencies
import React, { useState } from "react";
import { useIntl } from "react-intl";
import { standardTaskAPI } from "../../../../api";
import { isEmpty } from "lodash-es";
import { useQuery } from "../../../../hooks";
import { useDebounceCallback } from "../../../../hooks/useDebounceCallback.js";
import { INTERACTION_SUBTYPES, INTERACTION_TYPES } from "../../../../consts";

// Redux Dependencies
import { useSelector, useDispatch } from "react-redux";
import { selectCurrentText } from "../../../../redux/textsSlice";
import {
  selectAnswerComment,
  updateInteraction
} from "../../../../redux/interactionsSlice";
import { selectSubmission } from "../../../../redux/tasksSlice";
import { addSnackbar } from "../../../../redux/snackbarSlice.js";
import { handleRichText } from "../../../SharedComponents/textEditor/utils";

// Components
import { Box, Typography } from "@mui/material";
import NewTextEditor from "../../../SharedComponents/textEditor/NewTextEditor";

/**
/**
 * Calculate editor styles based on container width with proper constraints
 * @param {number} width - Container width in pixels
 * @returns {Object} Editor style object
 */
const getEditorStyles = (width) => ({
  width: width && width > 0 ? `${Math.min(width, 1000)}px` : "800px",
  maxWidth: "100%", // Ensure it never exceeds container
  overflow: "hidden", // Prevent horizontal overflow
  wordWrap: "break-word", // Enable word wrapping
  whiteSpace: "pre-wrap" // Preserve whitespace but wrap text
});

const AnswerView = ({ question, width }) => {
  const dispatch = useDispatch();
  const { submission_id } = useQuery();
  const intl = useIntl();
  const [isSaving, setIsSaving] = useState(false);

  // Redux selectors
  const submission = useSelector((state) =>
    selectSubmission(state, Number(submission_id))
  );
  const currentQuestionId = useSelector(
    (state) => state.interactions.selectedInteractionId
  );
  const answer = useSelector((state) =>
    selectAnswerComment(state, currentQuestionId)
  );
  const text = useSelector(selectCurrentText);

  // Derived values
  const isSubmitted = submission.status === "Submitted";
  const editorStyles = getEditorStyles(width);

  /* ... rest of component remains the same ... */

  /**
   * Save answer to backend or update in Redux
   * @param {Object} content - Editor content object with richText, plainText and wordCount
   */
  const saveAnswer = async (content) => {
    const { richText, plainText, wordCount } = content;

    // Create new answer if it doesn't exist, otherwise update existing one
    if (isEmpty(answer)) {
      return standardTaskAPI.createAnswerComment({
        content: plainText,
        rich_text: richText,
        word_count: wordCount,
        interaction_id: currentQuestionId,
        text_id: text.id,
        course_id: text.course_id,
        submission_id: submission.id,
        task_id: submission.task_id,
        interaction_type: INTERACTION_TYPES.ANSWER,
        interaction_subtype: INTERACTION_SUBTYPES.COMMENT
      });
    }

    return dispatch(
      updateInteraction({
        disableRollback: true,
        interaction: answer,
        update: {
          content: plainText,
          rich_text: richText,
          word_count: wordCount
        }
      })
    );
  };

  // Debounced save handler to prevent excessive API calls
  const debouncedSave = useDebounceCallback(async (content) => {
    if (isSaving) return;

    setIsSaving(true);
    try {
      await saveAnswer(content);
    } catch (error) {
      console.error("Failed to save content:", error);
      dispatch(
        addSnackbar({
          message: "Failed to save your answer. Please try again.",
          severity: "error"
        })
      );
    } finally {
      setIsSaving(false);
    }
  });

  // Create saving indicator component
  const SavingIndicator = () =>
    isSaving ? (
      <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
        Saving...
      </Typography>
    ) : null;

  return (
    <Box sx={{ width: "100%", overflow: "hidden" }}>
      <NewTextEditor
        key={question?.id || "placeholder"}
        initialContent={handleRichText(answer?.rich_text)}
        placeholder={intl.formatMessage({
          id: "task.open.placeholder",
          defaultMessage: "Type your written response here"
        })}
        onContentChange={debouncedSave}
        toolbar="fixed"
        showWordCount={true}
        disabled={isSubmitted}
        editorStyles={editorStyles}
        actions={
          <Box>
            <SavingIndicator />
          </Box>
        }
        fullHeight
      />
    </Box>
  );
};
export default AnswerView;
